import { Link } from 'react-router-dom'
import type { ComponentType, ReactNode } from 'react'
import {
  ArrowLeft,
  Ban,
  CheckCircle2,
  Database,
  FileText,
  Globe2,
  KeyRound,
  Lock,
  Mail,
  RefreshCw,
  Scale,
  ShieldCheck,
  Trash2,
  UsersRound,
} from 'lucide-react'

const lastUpdated = 'May 17, 2026'
const privacyEmail = 'privacy@zuhabul.com'
const hostedDomain = 'https://socialscale.zuhabul.com'

const googleData = [
  'YouTube channel identifiers, channel title, thumbnails, and basic channel metadata.',
  'OAuth access and refresh tokens needed to keep a connected YouTube channel authorized.',
  'Video metadata and upload actions when an operator asks SocialScale to draft, schedule, or publish YouTube content.',
]

const metaData = [
  'Facebook profile ID, name, email, and profile picture returned during authorization.',
  'Facebook Page and Instagram Business account identifiers, page names, categories, and connection status.',
  'Page access tokens, selected permissions, post/comment metadata, engagement signals, and publishing actions needed to run approved workflows.',
]

const deletionSteps = [
  'Disconnect the platform account inside SocialScale Settings > Connections when available.',
  'Revoke Google access from your Google Account permissions page.',
  'Remove SocialScale from Facebook Apps and Websites or Business Integrations.',
  `Email ${privacyEmail} with the workspace, account email, and platforms you want deleted.`,
]

const prohibitedUses = [
  'Using SocialScale to break any law, platform rule, advertising rule, intellectual property right, privacy right, or security requirement.',
  'Connecting accounts or data you do not own, administer, or have written authorization to manage.',
  'Using platform data for surveillance, data brokering, unauthorized profiling, model training outside requested workflows, or selling user data.',
  'Publishing deceptive, fraudulent, harmful, hateful, harassing, spam, malware, or impersonation content.',
  'Bypassing approval gates, rate limits, access controls, audit logs, or safety controls.',
]

function LegalShell({
  title,
  eyebrow,
  description,
  children,
}: {
  title: string
  eyebrow: string
  description: string
  children: ReactNode
}) {
  return (
    <div className="min-h-screen bg-surface text-slate-200">
      <header className="border-b border-surface-border bg-surface/95">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-800 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to SocialScale
          </Link>
          <div className="text-right text-xs text-slate-500">
            <div>Last updated</div>
            <div className="font-medium text-slate-300">{lastUpdated}</div>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:py-14">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-300">{eyebrow}</p>
          <h1 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl">{title}</h1>
          <p className="mt-4 text-base leading-7 text-slate-300">{description}</p>
        </div>
        {children}
      </main>
    </div>
  )
}

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: ComponentType<{ className?: string }>
  title: string
  children: ReactNode
}) {
  return (
    <section className="mt-8 border-t border-surface-border pt-8">
      <div className="mb-4 flex items-center gap-3">
        <div className="grid h-9 w-9 place-items-center rounded-lg border border-brand-500/30 bg-brand-500/10 text-brand-200">
          <Icon className="h-4 w-4" />
        </div>
        <h2 className="text-xl font-semibold text-white">{title}</h2>
      </div>
      <div className="space-y-4 text-sm leading-7 text-slate-300">{children}</div>
    </section>
  )
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

export default function PrivacyPolicyView() {
  return (
    <LegalShell
      eyebrow="Privacy policy"
      title="SocialScale Privacy Policy"
      description="This policy explains how the hosted SocialScale dashboard collects, uses, stores, shares, and deletes data for connected social platforms, including Meta and Google integrations."
    >
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-surface-border bg-surface-card p-4">
          <div className="text-sm font-semibold text-white">Public URL</div>
          <div className="mt-1 break-words text-sm text-slate-400">{hostedDomain}/privacy</div>
        </div>
        <div className="rounded-lg border border-surface-border bg-surface-card p-4">
          <div className="text-sm font-semibold text-white">Data deletion URL</div>
          <div className="mt-1 break-words text-sm text-slate-400">{hostedDomain}/data-deletion</div>
        </div>
        <div className="rounded-lg border border-surface-border bg-surface-card p-4">
          <div className="text-sm font-semibold text-white">Privacy contact</div>
          <a className="mt-1 block break-words text-sm text-brand-300 hover:text-brand-200" href={`mailto:${privacyEmail}`}>
            {privacyEmail}
          </a>
        </div>
      </div>

      <Section icon={UsersRound} title="Who We Are">
        <p>
          SocialScale is an autonomous social media operations platform for managing campaigns, content workflows,
          approvals, inbox activity, ads, analytics, and agent-assisted marketing work. This notice applies to the
          hosted SocialScale deployment at <span className="text-slate-100">{hostedDomain}</span>. Self-hosted
          deployments should publish their own controller and contact details if they operate under a different entity.
        </p>
      </Section>

      <Section icon={Database} title="Information We Collect">
        <p>We collect only the information needed to operate SocialScale and the platform connections an operator authorizes.</p>
        <BulletList
          items={[
            'Account and workspace data such as name, work email, role, workspace membership, and authentication records.',
            'Campaign, brand, draft, media, approval, schedule, inbox, analytics, and audit-log data entered or generated in the dashboard.',
            'Connected platform data from OAuth providers such as Meta, Google, X, LinkedIn, TikTok, Pinterest, Reddit, and similar services.',
            'Operational logs such as IP address, browser metadata, API errors, security events, and websocket connection state.',
            'AI workflow inputs and outputs needed to draft, review, transform, score, or summarize content under operator control.',
          ]}
        />
      </Section>

      <Section icon={KeyRound} title="Meta Platform Data">
        <p>
          When you connect Facebook or Instagram, SocialScale uses Meta OAuth permissions only for user-facing features
          such as page selection, publishing approved posts, reading engagement, managing approved comment workflows,
          and showing connection health.
        </p>
        <BulletList items={metaData} />
        <p>
          SocialScale does not sell Meta Platform Data, does not use it for surveillance, and does not combine it with
          unrelated data broker datasets. Operators can revoke access through Meta settings or request deletion as
          described below.
        </p>
      </Section>

      <Section icon={KeyRound} title="Google User Data">
        <p>
          When you connect Google or YouTube, SocialScale uses Google OAuth access only for the features visible in the
          dashboard, including channel connection, approved video upload workflows, and channel read access needed to
          confirm the authorized account.
        </p>
        <BulletList items={googleData} />
        <p>
          SocialScale uses Google user data only as disclosed in this policy and for providing or improving user-facing
          SocialScale features. We do not sell Google user data, transfer it to advertising networks, use it for
          surveillance, or use it to train generalized AI models. If an operator runs an AI feature on selected
          connected-account content, that processing is limited to the requested SocialScale workflow.
        </p>
      </Section>

      <Section icon={ShieldCheck} title="How We Use Information">
        <BulletList
          items={[
            'Provide the dashboard, CLI, API, agent workflows, approvals, scheduling, publishing, inbox, ads, reporting, and compliance features.',
            'Store encrypted OAuth credentials and refresh connected-account access when the operator keeps an integration enabled.',
            'Generate drafts, recommendations, reports, alerts, and performance summaries requested by the operator.',
            'Protect the service with authentication, rate limiting, audit logs, abuse detection, and incident investigation.',
            'Comply with legal obligations, platform terms, security requirements, and user deletion or access requests.',
          ]}
        />
      </Section>

      <Section icon={Lock} title="Storage, Security, and Sharing">
        <p>
          OAuth tokens and secrets are stored in the SocialScale credential vault and protected in transit with HTTPS.
          Access is role-based and intended for authorized workspace operators only.
        </p>
        <BulletList
          items={[
            'We share data with connected platforms only to perform operator-authorized actions, such as publishing or reading authorized account metadata.',
            'We use hosting, database, observability, security, and AI-processing providers as service providers when needed to operate SocialScale.',
            'We may disclose limited data if required by law, to prevent harm, or to protect SocialScale, users, and platform integrity.',
            'We do not sell personal data or platform data.',
          ]}
        />
      </Section>

      <Section icon={RefreshCw} title="Retention">
        <p>
          SocialScale keeps data for as long as needed to provide the service, maintain auditability, satisfy legal
          obligations, and protect the platform. OAuth pending states are short-lived, connected-account tokens are kept
          only while the integration remains enabled, and operational logs are retained for security and reliability.
          Deletion requests are handled promptly, normally within 30 days. Backup copies may take up to 90 days to age
          out unless longer retention is required by law or a security investigation.
        </p>
      </Section>

      <Section icon={Trash2} title="Your Choices and Deletion Rights">
        <p>You can disconnect integrations, revoke provider access, or request deletion of SocialScale-held data.</p>
        <BulletList items={deletionSteps} />
        <p>
          For Meta app review and user deletion flows, the public data deletion instructions are available at{' '}
          <Link to="/data-deletion" className="text-brand-300 hover:text-brand-200">
            {hostedDomain}/data-deletion
          </Link>
          .
        </p>
      </Section>

      <Section icon={Mail} title="Contact and Updates">
        <p>
          For privacy questions, access requests, correction requests, deletion requests, or concerns about Google or
          Meta platform data, contact{' '}
          <a className="text-brand-300 hover:text-brand-200" href={`mailto:${privacyEmail}`}>
            {privacyEmail}
          </a>
          . We will update this page when SocialScale changes how it accesses, uses, stores, shares, or deletes user
          data.
        </p>
      </Section>
    </LegalShell>
  )
}

export function DataDeletionView() {
  return (
    <LegalShell
      eyebrow="Data deletion"
      title="SocialScale Data Deletion Instructions"
      description="Use this page to disconnect provider access or request deletion of data SocialScale stores for your account, workspace, and connected Meta or Google integrations."
    >
      <Section icon={Trash2} title="How to Request Deletion">
        <BulletList items={deletionSteps} />
        <p>
          Include the workspace name, your account email, the connected platform, and whether you want only that
          integration deleted or the full SocialScale account/workspace data deleted.
        </p>
      </Section>

      <Section icon={KeyRound} title="Meta Removal Path">
        <p>
          If you connected Facebook or Instagram, you can remove SocialScale from Facebook Settings &gt; Apps and
          Websites or Business Integrations. When SocialScale receives a verified deletion request for a matched user,
          it deletes the stored Meta identifiers, tokens, connection records, and cached platform data associated with
          that identity.
        </p>
      </Section>

      <Section icon={KeyRound} title="Google Removal Path">
        <p>
          If you connected YouTube or another Google account, revoke SocialScale access from your Google Account
          permissions page. You may also email us to delete stored Google identifiers, OAuth tokens, connection records,
          and cached channel data associated with your SocialScale workspace.
        </p>
      </Section>

      <Section icon={RefreshCw} title="Deletion Timeline">
        <p>
          We normally complete verified deletion requests within 30 days. Some security logs, invoices, legal records,
          or backup copies may be retained longer when required for legitimate legal, security, fraud-prevention, or
          disaster-recovery reasons. Backup copies are normally removed within 90 days.
        </p>
      </Section>

      <Section icon={Mail} title="Confirmation">
        <p>
          We will confirm receipt and completion by email when the request is submitted directly to{' '}
          <a className="text-brand-300 hover:text-brand-200" href={`mailto:${privacyEmail}`}>
            {privacyEmail}
          </a>
          . If a platform callback provides a confirmation code, keep that code for your records.
        </p>
      </Section>
    </LegalShell>
  )
}

export function TermsConditionsView() {
  return (
    <LegalShell
      eyebrow="Terms and conditions"
      title="SocialScale Terms and Conditions"
      description="These terms govern access to the hosted SocialScale dashboard, API, connected platform workflows, and agent-assisted marketing operations."
    >
      <div className="mt-8 grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-surface-border bg-surface-card p-4">
          <div className="text-sm font-semibold text-white">Terms URL</div>
          <div className="mt-1 break-words text-sm text-slate-400">{hostedDomain}/terms</div>
        </div>
        <div className="rounded-lg border border-surface-border bg-surface-card p-4">
          <div className="text-sm font-semibold text-white">Privacy URL</div>
          <Link to="/privacy" className="mt-1 block break-words text-sm text-brand-300 hover:text-brand-200">
            {hostedDomain}/privacy
          </Link>
        </div>
        <div className="rounded-lg border border-surface-border bg-surface-card p-4">
          <div className="text-sm font-semibold text-white">Contact</div>
          <a className="mt-1 block break-words text-sm text-brand-300 hover:text-brand-200" href={`mailto:${privacyEmail}`}>
            {privacyEmail}
          </a>
        </div>
      </div>

      <Section icon={FileText} title="Agreement to These Terms">
        <p>
          By creating an account, signing in, connecting a platform account, using the API, or using a SocialScale
          workflow, you agree to these terms. If your organization has a signed written agreement with SocialScale, that
          agreement controls where it conflicts with these public terms.
        </p>
      </Section>

      <Section icon={UsersRound} title="Accounts and Authority">
        <BulletList
          items={[
            'You must provide accurate account, workspace, and contact information.',
            'You are responsible for keeping passwords, API keys, OAuth credentials, and session access secure.',
            'You may connect only social, ad, analytics, or business accounts that you own, administer, or are authorized to manage.',
            'You are responsible for all actions taken from your account, workspace, API keys, connected integrations, and configured agents.',
          ]}
        />
      </Section>

      <Section icon={Globe2} title="Platform Integrations">
        <p>
          SocialScale connects to third-party platforms such as Meta, Google, YouTube, X, LinkedIn, TikTok, Pinterest,
          Reddit, WhatsApp providers, and similar services. Your use of each integration must comply with that
          platform's terms, developer policies, advertising policies, community rules, rate limits, permission grants,
          and data deletion requirements.
        </p>
        <BulletList
          items={[
            'SocialScale may stop, limit, or disconnect an integration when a provider revokes access, changes an API, requires review, or reports misuse.',
            'You must not use connected platform data in a way the provider forbids, including unauthorized sharing, selling, profiling, or combining with unrelated datasets.',
            'You are responsible for reviewing drafts, approvals, campaign settings, targeting, and published output before enabling autonomous or scheduled execution.',
          ]}
        />
      </Section>

      <Section icon={ShieldCheck} title="Agent-Assisted Workflows">
        <p>
          SocialScale provides automation, recommendations, generation, summarization, scheduling, triage, reporting,
          and agent-assisted marketing workflows. Outputs may be inaccurate, incomplete, delayed, or unsuitable for a
          particular legal, regulated, brand, or platform context.
        </p>
        <BulletList
          items={[
            'Human operators remain responsible for final review, approvals, publishing, campaign spend, audience targeting, and compliance decisions.',
            'You must configure guardrails, approval thresholds, budgets, kill switches, and workspace permissions appropriate for your organization.',
            'SocialScale is not legal, financial, medical, employment, tax, or advertising-compliance advice.',
          ]}
        />
      </Section>

      <Section icon={Lock} title="Content and Data Rights">
        <p>
          You keep ownership of your brand assets, drafts, media, campaign data, connected-account data, and other
          content you submit to SocialScale. You grant SocialScale the limited rights needed to host, process, secure,
          transmit, transform, display, and publish that content solely to provide the service and requested workflows.
        </p>
        <BulletList
          items={[
            'You confirm that you have the rights and permissions needed for content, media, claims, offers, audiences, and data you provide.',
            'You must not upload secrets, regulated personal data, or confidential third-party data unless your workspace is configured to handle it safely.',
            'SocialScale may generate operational logs and audit records to protect the service, verify actions, and support compliance reviews.',
          ]}
        />
      </Section>

      <Section icon={Ban} title="Prohibited Uses">
        <BulletList items={prohibitedUses} />
      </Section>

      <Section icon={Database} title="Availability and Changes">
        <p>
          SocialScale may change, suspend, remove, or limit features when needed for security, reliability, provider API
          changes, abuse prevention, legal compliance, or product development. Third-party platform outages, account
          reviews, token expiry, provider policy changes, or rejected permissions may interrupt connected workflows.
        </p>
      </Section>

      <Section icon={Scale} title="Disclaimers and Liability">
        <p>
          SocialScale is provided on an as-is and as-available basis except where a separate written agreement says
          otherwise. To the maximum extent permitted by applicable law, SocialScale is not responsible for lost profits,
          lost data, failed campaigns, rejected platform reviews, suspended provider accounts, indirect damages, or
          consequences caused by user instructions, connected provider behavior, or unauthorized account access.
        </p>
      </Section>

      <Section icon={RefreshCw} title="Suspension and Termination">
        <p>
          We may suspend or terminate access if there is suspected misuse, security risk, nonpayment where billing
          applies, legal exposure, platform-policy violation, or material breach of these terms. You may stop using the
          service at any time and request deletion using the instructions at{' '}
          <Link to="/data-deletion" className="text-brand-300 hover:text-brand-200">
            {hostedDomain}/data-deletion
          </Link>
          .
        </p>
      </Section>

      <Section icon={Mail} title="Updates and Contact">
        <p>
          We may update these terms as SocialScale changes. The updated date on this page shows when the terms last
          changed. For terms, privacy, or deletion questions, contact{' '}
          <a className="text-brand-300 hover:text-brand-200" href={`mailto:${privacyEmail}`}>
            {privacyEmail}
          </a>
          .
        </p>
      </Section>
    </LegalShell>
  )
}
